import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import { VStack, Section } from 'tio-ui/components/layout';
import { Heading, Text } from 'tio-ui/components/typography';
import Chip from 'tio-reimagined/components/chip';
import Button from 'tio-reimagined/components/button';
import PageHero from 'tio-reimagined/components/page-hero';
import Drawer from 'tio-reimagined/components/drawer';
import { PhArrowLeft, PhSidebar } from 'ember-phosphor-icons';

export default class DrawerDetailPage extends Component {
  @tracked isOpen = false;
  @tracked placement: 'left' | 'right' | 'top' | 'bottom' = 'right';
  @tracked size: 'sm' | 'md' | 'lg' | 'xl' | 'full' = 'md';

  openDrawer = (p: 'left' | 'right' | 'top' | 'bottom', s: 'sm' | 'md' | 'lg' | 'xl' | 'full') => {
    this.placement = p;
    this.size = s;
    this.isOpen = true;
  };

  closeDrawer = () => {
    this.isOpen = false;
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="w-full min-w-0">
      <div class="mb-4">
        <LinkTo
          @route="authenticated.reimagined.components"
          class="inline-flex items-center gap-2 text-violet-600 dark:text-violet-400 hover:text-violet-800 dark:hover:text-violet-300 font-medium"
        >
          <PhArrowLeft @size={{18}} />
          Back to Components
        </LinkTo>
      </div>

      <PageHero
        @title="Drawer Component"
        @subtitle="Slide-out panel overlay with backdrop, keyboard (Escape) support, close-on-outside-click, and named blocks."
        @icon={{PhSidebar}}
        @theme="blue"
        @decoration="dots"
      >
        <:actions>
          <Chip @intent="primary">4 Placements</Chip>
          <Chip @intent="success">5 Sizes</Chip>
        </:actions>
      </PageHero>

      <div class="space-y-8">
        <Section>
          <:header>
            <div class="flex items-center gap-2">
              <PhSidebar @size={{20}} class="text-violet-600" />
              <span>Drawer</span>
              <Chip @size="sm" @intent="primary">Overlay</Chip>
            </div>
          </:header>
          <:body>
            <Text @intent="muted" class="mb-4">
              Slide-out panel overlay with backdrop, keyboard (Escape) support,
              close-on-outside-click, and named blocks for header, body, and footer. 4 placements, 5
              sizes. Portaled to document.body for z-index safety.
            </Text>

            <div class="bg-gray-50 rounded-lg p-6 mb-4 space-y-6">
              <div>
                <Text class="font-semibold text-gray-700 text-sm mb-3">Right Placement:</Text>
                <div class="flex flex-wrap gap-2">
                  <Button
                    @intent="primary"
                    @appearance="outlined"
                    @size="sm"
                    {{on "click" (fn this.openDrawer "right" "sm")}}
                  >Right (sm)</Button>
                  <Button
                    @intent="primary"
                    @appearance="outlined"
                    @size="sm"
                    {{on "click" (fn this.openDrawer "right" "md")}}
                  >Right (md)</Button>
                  <Button
                    @intent="primary"
                    @appearance="outlined"
                    @size="sm"
                    {{on "click" (fn this.openDrawer "right" "lg")}}
                  >Right (lg)</Button>
                  <Button
                    @intent="primary"
                    @appearance="outlined"
                    @size="sm"
                    {{on "click" (fn this.openDrawer "right" "xl")}}
                  >Right (xl)</Button>
                  <Button
                    @intent="primary"
                    @appearance="outlined"
                    @size="sm"
                    {{on "click" (fn this.openDrawer "right" "full")}}
                  >Right (full)</Button>
                </div>
              </div>

              <div>
                <Text class="font-semibold text-gray-700 text-sm mb-3">Left Placement:</Text>
                <div class="flex flex-wrap gap-2">
                  <Button
                    @intent="primary"
                    @appearance="outlined"
                    @size="sm"
                    {{on "click" (fn this.openDrawer "left" "sm")}}
                  >Left (sm)</Button>
                  <Button
                    @intent="primary"
                    @appearance="outlined"
                    @size="sm"
                    {{on "click" (fn this.openDrawer "left" "md")}}
                  >Left (md)</Button>
                  <Button
                    @intent="primary"
                    @appearance="outlined"
                    @size="sm"
                    {{on "click" (fn this.openDrawer "left" "lg")}}
                  >Left (lg)</Button>
                </div>
              </div>

              <div>
                <Text class="font-semibold text-gray-700 text-sm mb-3">Top / Bottom:</Text>
                <div class="flex flex-wrap gap-2">
                  <Button
                    @intent="primary"
                    @appearance="outlined"
                    @size="sm"
                    {{on "click" (fn this.openDrawer "top" "sm")}}
                  >Top (sm)</Button>
                  <Button
                    @intent="primary"
                    @appearance="outlined"
                    @size="sm"
                    {{on "click" (fn this.openDrawer "top" "md")}}
                  >Top (md)</Button>
                  <Button
                    @intent="primary"
                    @appearance="outlined"
                    @size="sm"
                    {{on "click" (fn this.openDrawer "bottom" "md")}}
                  >Bottom (md)</Button>
                  <Button
                    @intent="primary"
                    @appearance="outlined"
                    @size="sm"
                    {{on "click" (fn this.openDrawer "bottom" "lg")}}
                  >Bottom (lg)</Button>
                </div>
              </div>
            </div>

            <Drawer
              @isOpen={{this.isOpen}}
              @onClose={{this.closeDrawer}}
              @placement={{this.placement}}
              @size={{this.size}}
            >
              <:header>
                <Heading @level={{3}}>Sample Drawer</Heading>
              </:header>
              <:body>
                <div class="space-y-4">
                  <Text>
                    Slides in from the
                    <strong>{{this.placement}}</strong>
                    at size
                    <strong>{{this.size}}</strong>.
                  </Text>
                  <Text @intent="muted">Press Escape, click backdrop, or use the close button.</Text>
                </div>
              </:body>
              <:footer>
                <div class="flex justify-end">
                  <Button @intent="primary" {{on "click" this.closeDrawer}}>Close</Button>
                </div>
              </:footer>
            </Drawer>

            <details class="border rounded-lg">
              <summary
                class="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100"
              >
                Code Examples
              </summary>
              <pre class="p-4 text-xs text-gray-700 bg-gray-50 overflow-x-auto border-t"><code
                >&lt;Drawer @isOpen=&#123;&#123;this.isOpen&#125;&#125;
                  @onClose=&#123;&#123;this.close&#125;&#125; @placement="right" @size="md" &gt;
                  &lt;:header&gt;&lt;Heading
                  @level=&#123;&#123;3&#125;&#125;&gt;Title&lt;/Heading&gt;&lt;/:header&gt;
                  &lt;:body&gt;Content&lt;/:body&gt; &lt;:footer&gt;Footer&lt;/:footer&gt;
                  &lt;/Drawer&gt;</code></pre>
            </details>
          </:body>
        </Section>
      </div>
    </VStack>
  </template>
}
